<template>
  <canvas class="chart__canvas" ref="chart" data-cy="canvas"></canvas>
</template>

<script setup lang="ts">
import { useChart } from '@/composables/useChart.js';
import { useColors } from '@/composables/useColors.ts';
import { useDate } from '@/composables/useDate.ts';
import { useUnits } from '@/composables/useUnits.ts';
import type { HistoryDataState } from '@/types/HistoryData';
import type { DateString } from '@/types/DateString';
import type { ChartConfiguration } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import type { ModelRef, TemplateRef } from 'vue';
import { computed, onMounted, useTemplateRef, watch } from 'vue';
import { useAccountStore } from '@/stores/useAccountStore';
import { useCubeStore } from '@/stores/useCubeStore';
import { useHistoryStore } from '@/stores/useHistoryStore';
import { storeToRefs } from 'pinia';

// TODO what are these extra properties even needed for?
type CustomChartDataSet = Chart.ChartDataSets & { negativeLabel: string, positiveLabel: string }

const BATTERY_SOC_LABEL = "Batterie-Ladestand";

const chartRef: TemplateRef<HTMLCanvasElement> = useTemplateRef('chart')
const { formatWattage, formatPercent } = useUnits()
const { updateDayChart } = useChart(chartRef)
const { moment } = useDate()

const {
  colorPrimary,
  colorSecondary,
  colorSecondaryHighContrast
} = useColors()

const { isHighContrast } = storeToRefs(useAccountStore())
const { showBatteryData, showInverterData, supplier } = storeToRefs(useCubeStore())
const { isMeterless } = storeToRefs(useHistoryStore())

const model = defineModel<HistoryDataState>();

const colorSupplier = computed(() => {
  if (isHighContrast.value) {
    return colorSecondaryHighContrast.value
  }
  return colorSecondary.value
})

function formatPrice2Fractions(value) {
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ct`
}


const chartConfig = computed(() => {
  const config: ChartConfiguration = {
    type: 'line',
    options: {
      maintainAspectRatio: false,
      // fontColor: '#dddddd',
      legend: {
        position: 'bottom',
        // onClick: function(e: MouseEvent, legendItem){
        //   const chart = (this as any).chart
        //   const index = legendItem.datasetIndex
        //   const socIndex = chart.data.datasets
        //     .findIndex(d => d.yAxisID === 'yPercent')

        //   const dataSetMeta = chart.getDatasetMeta(index)
        //   dataSetMeta.hidden = dataSetMeta.hidden === null
        //     ? !chart.data.datasets[index].hidden
        //     : null

        //   if (index == socIndex) {
        //     const yPercent = chart.options.scales.yAxes.find(a => a.id === 'yPercent')

        //     if (yPercent) {
        //       yPercent.display = dataSetMeta.hidden != null
        //     }
        //   }

        //   chart.update()
        // }
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
              zeroLineColor: 'transparent',
            },
            display: true,
            ticks: {
              autoSkip: true,
              autoSkipPadding: 5,
            },
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm',
              },
            },
            adapters: {
              date: {
                locale: de
              }
            },
          },
        ],
        yAxes: [
          {
            id: 'yAbove',
            stacked: true,
            ticks: {
              callback: (value: number) => {
                return formatWattage(Math.abs(value))
              },
              beginAtZero: true,
            },
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
              zeroLineWidth: 1,
              zeroLineColor: '#2C292E',
              z: 50,
            },
            type: 'linear',
          },
          {
            id: 'yNonStacked',
            stacked: false,
            display: false,
            ticks: {
              callback: (value: number) => {
                return formatWattage(Math.abs(value))
              },
              suggestedMax: 1,
              suggestedMin: 0,
              beginAtZero: true,
            },
            type: 'linear',
          },
          {
            id: 'yPercent',
            position: 'right',
            stacked: false,
            display: false,
            ticks: {
              callback: (value) => {
                return formatPercent(value)
              },
              suggestedMax: 100,
              suggestedMin: 0,
              beginAtZero: true,
            },
            gridLines: {
              drawOnChartArea: false
            },
            type: 'linear',
          },
        ],
      },
      hover: {
        mode: 'index',
        intersect: false,
      },
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (item: any, data) => {
            const dateString = data.labels[item[0].index] as DateString
            return moment(dateString).format(
              'DD.MM HH:mm'
            )
          },
          label: (item, data) => {
            const dataset = data.datasets[item.datasetIndex] as CustomChartDataSet
            const value = (item.yLabel + '').replace(/^-/, '')
            let label = dataset.label

            const yLabel = item.yLabel as number

            if (
              label.indexOf('Flex-Preis') === 0 ||
              label.indexOf('Strompreis bisher') === 0
            ) {
              return label + ': ' + formatPrice2Fractions(value)
            } else if (yLabel < 0 && dataset.negativeLabel) {
              label = dataset.negativeLabel
            } else if (yLabel > 0 && dataset.positiveLabel) {
              label = dataset.positiveLabel
            } else if (label == BATTERY_SOC_LABEL) {
              return label + ': ' + formatPercent(value)
            }

            return label + ': ' + formatWattage(value)
          },
        },
      },
    },
    data: {
      labels: [],
      datasets: [
        {
          label: 'Vom Marktplatz',
          fill: 'origin',
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          backgroundColor: colorPrimary.value,
          data: [],
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          label: 'Von ' + supplier.value.name,
          fill: 'origin',
          backgroundColor: colorSupplier.value,
          data: [],
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          label: 'An Marktplatz',
          fill: 'origin',
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          backgroundColor: colorPrimary.value,
          data: [],
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          label: 'An ' + supplier.value.name,
          fill: 'origin',
          backgroundColor: colorSupplier.value,
          data: [],
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          label: 'Überschuss',
          fill: 'origin',
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          data: [],
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
      ],
    },
  }

  if (showInverterData.value) {
    config.data.datasets.push({
      label: 'Wechselrichter Produktionsleistung',
      fill: false,
      borderWidth: 2,
      lineTension: 0.8,
      cubicInterpolationMode: 'monotone',
      pointRadius: 0,
      data: [],
      borderJoinStyle: 'round',
      borderColor: 'rgba(109, 130, 96, 0.8)',
      pointStyle: 'circle',
      yAxisID: 'yNonStacked',
    })
  }

  if (showBatteryData.value) {
    config.data.datasets.push({
      label: BATTERY_SOC_LABEL,
      fill: false,
      borderWidth: 2,
      lineTension: 0.8,
      cubicInterpolationMode: 'monotone',
      pointRadius: 0,
      data: [],
      borderJoinStyle: 'round',
      borderColor: 'rgba(255, 95, 95, 0.8)',
      pointStyle: 'circle',
      yAxisID: 'yPercent',
      hidden: false,
    })
  }

  if (isMeterless.value) {
    config.data.datasets.push({
      label: 'Prognose',
      fill: false,
      borderWidth: 2,
      lineTension: 0.8,
      cubicInterpolationMode: 'monotone',
      pointRadius: 0,
      data: [],
      borderJoinStyle: 'round',
      borderColor: 'rgba(109, 130, 96, 0.8)',
      pointStyle: 'circle',
      yAxisID: 'yNonStacked',
      spanGaps: false,
      borderDash: [20, 5],
    })
  }

  return config
})

watch(model, (data: HistoryDataState) => {
  updateDayChart(data, chartConfig.value)
});


</script>
